// AnimationMixer: семплирование GLB-клипов, FK по node hierarchy, наложение
// upper-body слоя поверх base-слоя (для "стрельбы на бегу").
//
// Поза = карта nodeIdx → локальная TRS (relative к parent). Если node отсутствует
// в позе — берётся rest-pose из NodeData. Композиция матриц: localMat = T·R·S
// (column-major), worldMat[i] = worldMat[parent(i)] · localMat[i],
// boneMat[j] = worldMat[skin.joints[j]] · inverseBindMatrix[j].

import type { AnimationClip, NodeData, SkinData } from "./meshLoader";

/** Кросс-фейд между двумя base-анимациями (idle ↔ walk и т.п.). */
export const CROSSFADE_DURATION = 0.15;

/** Fade-in/out для upper-layer (стрельба появляется/исчезает плавно). */
export const UPPER_FADE_DURATION = 0.10;

export type Vec3 = [number, number, number];
export type Quat = [number, number, number, number]; // xyzw

export interface PoseSample {
  /** nodeIdx → local translation; node не в map'е → rest-pose. */
  translation: Map<number, Vec3>;
  rotation: Map<number, Quat>;
  scale: Map<number, Vec3>;
}

export interface AnimLayer {
  clipName: string;
  /** -1..1: положительное = fade-in/up; отрицательное = fade-out (магнитуда = текущий weight). */
  weight: number;
  time: number;
  looping: boolean;
  /** Множитель скорости проигрывания (1.0 = нормально, 2.0 = в 2× быстрее). */
  speed: number;
  /** Опциональная кастомная маска (Uint8Array длиной skin.joints.length) —
   *  если задана, overlay'ит по ней ВМЕСТО `mesh.upperBodyMask`. Используется
   *  для hurt-flinch, где маска "корпус+голова" отличается от upper-body
   *  (upper-body включает руки; для hurt руки надо оставить в idle). */
  mask?: Uint8Array | null;
  /** Опциональный override для extraNodes (не-skin-joint ноды типа
   *  weapone_bone). Для shoot'а overlay'им оружие (default: mesh.upperBodyExtraNodes),
   *  для hurt — нет (передаём []). */
  extraNodes?: number[] | null;
}

export interface AnimState {
  baseAnim: string;
  baseTime: number;
  /** Множитель скорости базового клипа (вперёд / назад для дверей). */
  baseSpeed: number;
  /** При смене base — храним предыдущий и crossfadeT нарастает 0→1. */
  prevBase: string | null;
  prevBaseTime: number;
  crossfadeT: number; // 0..1
  /** Длительность текущего активного crossfade (сек). Может отличаться от
   *  глобального CROSSFADE_DURATION если был передан в setBaseAnim. */
  crossfadeDur: number;
  upperLayer: AnimLayer | null;
}

export function newAnimState(initialAnim: string): AnimState {
  return {
    baseAnim: initialAnim,
    baseTime: 0,
    baseSpeed: 1,
    prevBase: null,
    prevBaseTime: 0,
    crossfadeT: 1,
    crossfadeDur: CROSSFADE_DURATION,
    upperLayer: null,
  };
}

/** Сменить base-анимацию с cross-fade'ом. Если такая уже играет — ignore.
 *  Если возвращаемся к prevBase до окончания текущего кроссфейда —
 *  просто разворачиваем кроссфейд (не сбрасывая baseTime), чтобы
 *  быстрая болтанка walk↔run не рестартила бег с нулевого кадра.
 *
 *  Опциональный `duration` — персональная длительность crossfade (сек).
 *  Используется для быстрых транзиций где базовые позы клипов сильно
 *  отличаются и длинный blend даёт визуальный "пробег" Hips (runToStop). */
export function setBaseAnim(state: AnimState, name: string, duration?: number): void {
  if (state.baseAnim === name) return;
  const dur = duration ?? CROSSFADE_DURATION;

	if (state.prevBase === name && state.crossfadeT < 1) {
    const oldName = state.baseAnim;
    const oldTime = state.baseTime;
    state.baseAnim = name;
    state.baseTime = state.prevBaseTime;
    state.prevBase = oldName;
    state.prevBaseTime = oldTime;
    state.crossfadeT = 1 - state.crossfadeT;
    state.crossfadeDur = dur;
    state.baseSpeed = 1;
    return;
  }

  state.prevBase = state.baseAnim;
  state.prevBaseTime = state.baseTime;
  state.baseAnim = name;
  state.baseTime = 0;
  state.crossfadeT = 0;
  state.crossfadeDur = dur;
  state.baseSpeed = 1;
}

/** Запустить upper-layer (например shoot). Если уже есть — обновляем clip.
 *  `speed` — множитель скорости проигрывания (default 1.0).
 *  `mask`/`extraNodes` — опциональные override'ы для overlayPose. Если не
 *  заданы, при накладывании берутся из mesh.upperBodyMask / mesh.upperBodyExtraNodes. */
export function startUpperLayer(
  state: AnimState, clipName: string, looping: boolean,
  speed = 1.0,
  mask?: Uint8Array | null,
  extraNodes?: number[] | null,
): void {
  if (state.upperLayer && state.upperLayer.clipName === clipName && state.upperLayer.weight > 0) {
    // Уже играем тот же клип — только корректируем speed, weight/time не сбрасываем.
    state.upperLayer.speed = speed;
    if (mask !== undefined) state.upperLayer.mask = mask;
    if (extraNodes !== undefined) state.upperLayer.extraNodes = extraNodes;
    return;
  }
  state.upperLayer = {
    clipName, weight: 0, time: 0, looping, speed,
    mask: mask ?? null,
    extraNodes: extraNodes ?? null,
  };
}

/** Помечаем upper-layer для fade-out: weight уходит в отрицательную область,
 *  магнитуда показывает текущий вес, при достижении 0 layer удаляется. */
export function stopUpperLayer(state: AnimState): void {
  if (!state.upperLayer) return;
  if (state.upperLayer.weight <= 0) return; // уже fading out
  state.upperLayer.weight = -state.upperLayer.weight;
}

/** Тик AnimState (вызывать каждый кадр перед updateInstancePose). */
export function tickAnimState(state: AnimState, dt: number): void {
  state.baseTime += dt * state.baseSpeed;
  if (state.prevBase !== null) state.prevBaseTime += dt * state.baseSpeed;
  if (state.crossfadeT < 1) {
    if (state.crossfadeDur <= 0) {
      // Мгновенный snap — без blend'а между клипами.
      state.crossfadeT = 1;
      state.prevBase = null;
    } else {
      state.crossfadeT = Math.min(1, state.crossfadeT + dt / state.crossfadeDur);
      if (state.crossfadeT >= 1) state.prevBase = null;
    }
  }
  const u = state.upperLayer;
  if (u) {
    u.time += dt * u.speed;
    if (u.weight >= 0) {
      // Fade-in
      if (u.weight < 1) u.weight = Math.min(1, u.weight + dt / UPPER_FADE_DURATION);
    } else {
      // Fade-out: weight отрицательный, идёт к 0 (= layer удалён)
      u.weight = Math.min(0, u.weight + dt / UPPER_FADE_DURATION);
      if (u.weight >= 0) state.upperLayer = null;
    }
  }
}

// ─── sampling ─────────────────────────────────────────────────────────

/** Переиспользуемый набор ключей нод для blendPosesInto — без `new Set` каждый кадр. */
const scratchBlendKeys = new Set<number>();

/** Семплируем clip в `out` (maps очищаются); без аллокаций для hot path рендера.
 *  С `nodeFilter` — оверлей-режим: пишутся ТОЛЬКО каналы нод из фильтра и
 *  `out` НЕ очищается (запись поверх base-позы). Нужен для общих дверных
 *  клипов (DoorOpen таргетит полотна всех дверей — играем одну). */
export function sampleClipInto(
  clip: AnimationClip, time: number, looping: boolean, out: PoseSample,
  nodeFilter?: ReadonlySet<number>,
): void {
  let t = time;
  if (looping && clip.duration > 0) {
    t = ((time % clip.duration) + clip.duration) % clip.duration;
  } else {
    if (t < 0) t = 0;
    if (t > clip.duration) t = clip.duration;
  }

  if (!nodeFilter) {
    out.translation.clear();
    out.rotation.clear();
    out.scale.clear();
  }
  for (const ch of clip.channels) {
    if (nodeFilter && !nodeFilter.has(ch.targetNode)) continue;
    // Бинарный поиск ключа
    const times = ch.times;
    const n = times.length;
    if (n === 0) continue;
    let i0 = 0, i1 = 0, frac = 0;
    if (t <= times[0]) {
      i0 = i1 = 0; frac = 0;
    } else if (t >= times[n - 1]) {
      i0 = i1 = n - 1; frac = 0;
    } else {
      let lo = 0, hi = n - 1;
      while (hi - lo > 1) {
        const mid = (lo + hi) >> 1;
        if (times[mid] <= t) lo = mid; else hi = mid;
      }
      i0 = lo; i1 = hi;
      const span = times[i1] - times[i0];
      frac = span > 0 ? (t - times[i0]) / span : 0;
    }
    const k = ch.components;
    const v = ch.values;
    const a = i0 * k;
    const b = i1 * k;
    if (ch.path === "rotation") {
      const q: Quat = [
        v[a],     v[a + 1], v[a + 2], v[a + 3],
      ];
      const qb: Quat = [
        v[b],     v[b + 1], v[b + 2], v[b + 3],
      ];
      out.rotation.set(ch.targetNode, slerp(q, qb, frac));
    } else if (ch.path === "translation") {
      out.translation.set(ch.targetNode, [
        v[a]     + (v[b]     - v[a])     * frac,
        v[a + 1] + (v[b + 1] - v[a + 1]) * frac,
        v[a + 2] + (v[b + 2] - v[a + 2]) * frac,
      ]);
    } else {
      out.scale.set(ch.targetNode, [
        v[a]     + (v[b]     - v[a])     * frac,
        v[a + 1] + (v[b + 1] - v[a + 1]) * frac,
        v[a + 2] + (v[b + 2] - v[a + 2]) * frac,
      ]);
    }
  }
}

/** Семплируем clip в момент `time` (аллокация pose — для отладки/редких вызовов). */
export function sampleClip(clip: AnimationClip, time: number, looping: boolean): PoseSample {
  const pose: PoseSample = {
    translation: new Map(),
    rotation: new Map(),
    scale: new Map(),
  };
  sampleClipInto(clip, time, looping, pose);
  return pose;
}

/** Блендим a→b в `out` (maps очищаются). Hot path: без новых Map/Set. */
export function blendPosesInto(a: PoseSample, b: PoseSample, t: number, out: PoseSample): void {
  out.translation.clear();
  out.rotation.clear();
  out.scale.clear();
  const keys = scratchBlendKeys;
  keys.clear();
  for (const k of a.translation.keys()) keys.add(k);
  for (const k of b.translation.keys()) keys.add(k);
  for (const k of keys) {
    const av = a.translation.get(k);
    const bv = b.translation.get(k);
    if (av && bv) out.translation.set(k, lerp3(av, bv, t));
    else if (av) out.translation.set(k, av);
    else if (bv) out.translation.set(k, bv);
  }
  keys.clear();
  for (const k of a.rotation.keys()) keys.add(k);
  for (const k of b.rotation.keys()) keys.add(k);
  for (const k of keys) {
    const av = a.rotation.get(k);
    const bv = b.rotation.get(k);
    if (av && bv) out.rotation.set(k, slerp(av, bv, t));
    else if (av) out.rotation.set(k, av);
    else if (bv) out.rotation.set(k, bv);
  }
  keys.clear();
  for (const k of a.scale.keys()) keys.add(k);
  for (const k of b.scale.keys()) keys.add(k);
  for (const k of keys) {
    const av = a.scale.get(k);
    const bv = b.scale.get(k);
    if (av && bv) out.scale.set(k, lerp3(av, bv, t));
    else if (av) out.scale.set(k, av);
    else if (bv) out.scale.set(k, bv);
  }
}

/** Блендим две позы по single weight (LERP T/S, SLERP R). Аллокация out — редкие вызовы. */
export function blendPoses(a: PoseSample, b: PoseSample, t: number): PoseSample {
  const out: PoseSample = {
    translation: new Map(),
    rotation: new Map(),
    scale: new Map(),
  };
  blendPosesInto(a, b, t, out);
  return out;
}

/** Накладываем upper-pose на base-pose:
 *  - для skinJoints где mask=1 — полный overlay TRS (base.trs → slerp/lerp → upper.trs);
 *  - дополнительно для каждой ноды из extraNodes (например weapone_bone — это
 *    не skin-joint, но если мы его не overlay'нем, оружие застрянет в
 *    base-позе пока рука уходит в стрельбу, и визуально "отлипнет" от руки);
 *  - для каждой ноды из rotationOnlyNodes — только rotation (не TRS).
 *    Нужно для Hips: подтягиваем torso к "facing-forward" позе stance/shoot,
 *    но сохраняем translation base-клипа (вертикальный bob при ходьбе/беге,
 *    платформа/прыжок). Без этого strafe-клипы (Hips yaw до ±150°) крутят
 *    torso вокруг Hips и пистолет уводит в сторону от прицела.
 *  Модифицирует base in-place для эффективности. */
export function overlayPose(
  base: PoseSample,
  upper: PoseSample,
  mask: Uint8Array,
  weight: number,
  skinJoints: number[],
  extraNodes: number[] = [],
  rotationOnlyNodes: number[] = [],
): void {
  const w = Math.max(0, Math.min(1, weight));
  if (w <= 0) return;
  for (let j = 0; j < skinJoints.length; j++) {
    if (mask[j] === 0) continue;
    overlayNodePose(base, upper, skinJoints[j], w);
  }
  for (const node of extraNodes) {
    overlayNodePose(base, upper, node, w);
  }
  for (const node of rotationOnlyNodes) {
    overlayNodeRotationOnly(base, upper, node, w);
  }
}

function overlayNodePose(
  base: PoseSample, upper: PoseSample, node: number, w: number,
): void {
  const ut = upper.translation.get(node);
  if (ut) {
    const bt = base.translation.get(node);
    base.translation.set(node, bt ? lerp3(bt, ut, w) : ut);
  }
  const ur = upper.rotation.get(node);
  if (ur) {
    const br = base.rotation.get(node);
    base.rotation.set(node, br ? slerp(br, ur, w) : ur);
  }
  const us = upper.scale.get(node);
  if (us) {
    const bs = base.scale.get(node);
    base.scale.set(node, bs ? lerp3(bs, us, w) : us);
  }
}

/** Замещает только rotation узла — translation/scale остаются из base. */
function overlayNodeRotationOnly(
  base: PoseSample, upper: PoseSample, node: number, w: number,
): void {
  const ur = upper.rotation.get(node);
  if (!ur) return;
  const br = base.rotation.get(node);
  base.rotation.set(node, br ? slerp(br, ur, w) : ur);
}

/** Aim IK: добавляет вертикальный наклон (pitch) и горизонтальный твист (yaw)
 *  корпуса поверх существующей позы spine-кости.
 *  - pitch > 0 = смотреть вверх, < 0 = вниз
 *  - yawTwist > 0 = повернуть корпус ВЛЕВО (в world frame), < 0 = ВПРАВО.
 *    Используется чтобы компенсировать запечённый угол ствола в анимации:
 *    например firingRifle направляет ствол на ~40° левее естественного
 *    forward'а — задаём `yawTwist = -40°`, корпус доворачивается вправо,
 *    оружие выравнивается на крест.
 *
 *  Mixamo bone convention: +Y вдоль кости (от родителя к ребёнку),
 *  +X — лат. ось (поворот вокруг неё = pitch), +Z — твист (вокруг него = yaw).
 *  ВАЖНО: знаки осей и порядок поворотов проверены экспериментально —
 *  Mixamo Spine после Armature Rx(90°) даёт нестандартные направления.
 *
 *  Применяется ДО FK, поэтому изменение распространяется на всё, что выше
 *  spine-кости (плечи, голова, руки, оружие через weapone_bone subtree). */
export function applyAimToPose(
  pose: PoseSample,
  mesh: { aimPitchBoneIdx: number | null; nodes: { rotation: [number, number, number, number] }[] },
  pitch: number,
  yawTwist: number,
): void {
  const idx = mesh.aimPitchBoneIdx;
  if (idx === null) return;

  // Pitch: вокруг bone-local +X. Знак инвертирован — Spine после
  // Armature Rx(90°) реагирует "вверх ногами" на положительный поворот.
  const halfP = -pitch * 0.5;
  const qPitch: Quat = [Math.sin(halfP), 0, 0, Math.cos(halfP)];

  // Yaw twist: вокруг bone-local +Y (вдоль длины кости spine).
  const halfY = yawTwist * 0.5;
  const qYaw: Quat = [0, Math.sin(halfY), 0, Math.cos(halfY)];

  const cur = pose.rotation.get(idx) ?? mesh.nodes[idx].rotation;
  // Порядок: сначала yaw-твист (горизонтальный поворот корпуса), затем pitch
  // (наклон уже повёрнутого корпуса). new = cur · qYaw · qPitch.
  pose.rotation.set(idx, quatMul(quatMul(cur, qYaw), qPitch));
}

// Старое имя для обратной совместимости / clarity при нулевом yaw.
export function applyAimPitchToPose(
  pose: PoseSample,
  mesh: { aimPitchBoneIdx: number | null; nodes: { rotation: [number, number, number, number] }[] },
  pitch: number,
): void {
  applyAimToPose(pose, mesh, pitch, 0);
}

/** Quaternion multiply (xyzw). result = a · b. */
function quatMul(a: Quat, b: Quat): Quat {
  return [
    a[3]*b[0] + a[0]*b[3] + a[1]*b[2] - a[2]*b[1],
    a[3]*b[1] - a[0]*b[2] + a[1]*b[3] + a[2]*b[0],
    a[3]*b[2] + a[0]*b[1] - a[1]*b[0] + a[2]*b[3],
    a[3]*b[3] - a[0]*b[0] - a[1]*b[1] - a[2]*b[2],
  ];
}

// ─── FK ────────────────────────────────────────────────────────────────

/** Поза inner-иерархии: ноды с тем же `name`, что у outer, получают TRS из
 *  уже семплированной outer-позы; остальные остаются на rest из NodeData.
 *  Нужно для GLB где воксели — отдельные ноды под костями (индексы inner≠outer). */
export function buildInnerPoseFromOuter(
  innerNodes: NodeData[],
  outerNodes: NodeData[],
  outerPose: PoseSample,
  out: PoseSample,
): void {
  out.translation.clear();
  out.rotation.clear();
  out.scale.clear();
  const outerByName = new Map<string, number>();
  for (let j = 0; j < outerNodes.length; j++) {
    const nm = outerNodes[j]!.name;
    if (!outerByName.has(nm)) outerByName.set(nm, j);
  }
  for (let i = 0; i < innerNodes.length; i++) {
    const j = outerByName.get(innerNodes[i]!.name);
    if (j === undefined) continue;
    const t = outerPose.translation.get(j);
    if (t) out.translation.set(i, t);
    const r = outerPose.rotation.get(j);
    if (r) out.rotation.set(i, r);
    const sc = outerPose.scale.get(j);
    if (sc) out.scale.set(i, sc);
  }
}

/** Вычисляет world-matrix для каждого node (forward kinematics).
 *  Обход — строго в `traversalOrder` (родитель ВСЕГДА раньше потомка).
 *  Этот порядок строится один раз в `meshLoader.buildTraversalOrder` и
 *  не зависит от того, в каком порядке glTF положил ноды в массив.
 *
 *  out: Float32Array длиной nodes.length * 16 (column-major). */
export function computeNodeWorldMatrices(
  nodes: NodeData[],
  traversalOrder: number[],
  pose: PoseSample,
  out: Float32Array,
): void {
  const tmp = new Float32Array(16);
  for (let k = 0; k < traversalOrder.length; k++) {
    const i = traversalOrder[k];
    const n = nodes[i];
    const t = pose.translation.get(i) ?? n.translation;
    const r = pose.rotation.get(i) ?? n.rotation;
    const s = pose.scale.get(i) ?? n.scale;
    composeMat4(tmp, t, r, s);
    if (n.parent < 0) {
      out.set(tmp, i * 16);
    } else {
      multiplyMat4Subarray(out, n.parent * 16, tmp, out, i * 16);
    }
  }
}

/** boneMatrices[j] = nodeWorld[skin.joints[j]] · inverseBindMatrix[j].
 *  Для Mixamo-style риг (POSITION уже в armature-world frame, IBM содержит
 *  scale 100 для компенсации Armature scale 0.01) дополнительный M⁻¹ не нужен:
 *  на rest pose nodeWorld·IBM = identity и вершины остаются на месте. */
export function computeBoneMatrices(
  skin: SkinData,
  nodeWorld: Float32Array,
  out: Float32Array,
): void {
  const ibm = skin.inverseBindMatrices;
  const tmp = new Float32Array(16);
  const tmp2 = new Float32Array(16);
  for (let j = 0; j < skin.joints.length; j++) {
    const nw = nodeWorld.subarray(skin.joints[j] * 16, skin.joints[j] * 16 + 16);
    const ib = ibm.subarray(j * 16, j * 16 + 16);
    tmp.set(nw);
    tmp2.set(ib);
    multiplyMat4Direct(tmp, tmp2, out, j * 16);
  }
}

/** Кости inner: world-матрица берётся по имени из позы outer (nodeWorld длины outer.nodes). */
export function computeBoneMatricesInnerFromOuterPose(
  innerSkin: SkinData,
  innerNodes: NodeData[],
  outerNodes: NodeData[],
  nodeWorldFromOuter: Float32Array,
  out: Float32Array,
): void {
  const nameToOuterIdx = new Map<string, number>();
  for (let i = 0; i < outerNodes.length; i++) {
    const nm = outerNodes[i]!.name;
    if (!nameToOuterIdx.has(nm)) nameToOuterIdx.set(nm, i);
  }
  const ibm = innerSkin.inverseBindMatrices;
  const tmp = new Float32Array(16);
  const tmp2 = new Float32Array(16);
  const ident = new Float32Array([1, 0, 0, 0, 0, 1, 0, 0, 0, 0, 1, 0, 0, 0, 0, 1]);
  for (let j = 0; j < innerSkin.joints.length; j++) {
    const inn = innerSkin.joints[j];
    const boneName = innerNodes[inn]!.name;
    const oi = nameToOuterIdx.get(boneName);
    const nw = oi !== undefined
      ? nodeWorldFromOuter.subarray(oi * 16, oi * 16 + 16)
      : ident;
    tmp.set(nw);
    tmp2.set(ibm.subarray(j * 16, j * 16 + 16));
    multiplyMat4Direct(tmp, tmp2, out, j * 16);
  }
}

// ─── Math helpers ──────────────────────────────────────────────────────

function lerp3(a: Vec3, b: Vec3, t: number): Vec3 {
  return [
    a[0] + (b[0] - a[0]) * t,
    a[1] + (b[1] - a[1]) * t,
    a[2] + (b[2] - a[2]) * t,
  ];
}

/** SLERP двух кватернионов (xyzw). Если dot < 0 — берём противоположный b
 *  для кратчайшего пути. */
function slerp(a: Quat, b: Quat, t: number): Quat {
  let bx = b[0], by = b[1], bz = b[2], bw = b[3];
  let dot = a[0] * bx + a[1] * by + a[2] * bz + a[3] * bw;
  if (dot < 0) {
    bx = -bx; by = -by; bz = -bz; bw = -bw;
    dot = -dot;
  }
  if (dot > 0.9995) {
    // Линейный fallback и нормализация для очень близких кватернионов
    const x = a[0] + (bx - a[0]) * t;
    const y = a[1] + (by - a[1]) * t;
    const z = a[2] + (bz - a[2]) * t;
    const w = a[3] + (bw - a[3]) * t;
    const len = Math.hypot(x, y, z, w) || 1;
    return [x / len, y / len, z / len, w / len];
  }
  const theta = Math.acos(dot);
  const sinT = Math.sin(theta);
  const s0 = Math.sin((1 - t) * theta) / sinT;
  const s1 = Math.sin(t * theta) / sinT;
  return [
    a[0] * s0 + bx * s1,
    a[1] * s0 + by * s1,
    a[2] * s0 + bz * s1,
    a[3] * s0 + bw * s1,
  ];
}

/** mat4 = T·R·S (column-major). out длиной 16. */
function composeMat4(out: Float32Array, t: Vec3, r: Quat, s: Vec3): void {
  const x = r[0], y = r[1], z = r[2], w = r[3];
  const x2 = x + x, y2 = y + y, z2 = z + z;
  const xx = x * x2, xy = x * y2, xz = x * z2;
  const yy = y * y2, yz = y * z2, zz = z * z2;
  const wx = w * x2, wy = w * y2, wz = w * z2;
  const sx = s[0], sy = s[1], sz = s[2];

  out[0]  = (1 - (yy + zz)) * sx;
  out[1]  = (xy + wz) * sx;
  out[2]  = (xz - wy) * sx;
  out[3]  = 0;

  out[4]  = (xy - wz) * sy;
  out[5]  = (1 - (xx + zz)) * sy;
  out[6]  = (yz + wx) * sy;
  out[7]  = 0;

  out[8]  = (xz + wy) * sz;
  out[9]  = (yz - wx) * sz;
  out[10] = (1 - (xx + yy)) * sz;
  out[11] = 0;

  out[12] = t[0];
  out[13] = t[1];
  out[14] = t[2];
  out[15] = 1;
}

/** out[outOffset..+16] = a[aOffset..+16] * b (column-major).
 *  Принимает большой Float32Array и offset'ы для эффективности. */
function multiplyMat4Subarray(
  a: Float32Array, aOffset: number,
  b: Float32Array,
  out: Float32Array, outOffset: number,
): void {
  const a00 = a[aOffset + 0],  a01 = a[aOffset + 1],  a02 = a[aOffset + 2],  a03 = a[aOffset + 3];
  const a10 = a[aOffset + 4],  a11 = a[aOffset + 5],  a12 = a[aOffset + 6],  a13 = a[aOffset + 7];
  const a20 = a[aOffset + 8],  a21 = a[aOffset + 9],  a22 = a[aOffset + 10], a23 = a[aOffset + 11];
  const a30 = a[aOffset + 12], a31 = a[aOffset + 13], a32 = a[aOffset + 14], a33 = a[aOffset + 15];
  for (let i = 0; i < 4; i++) {
    const b0 = b[i * 4], b1 = b[i * 4 + 1], b2 = b[i * 4 + 2], b3 = b[i * 4 + 3];
    out[outOffset + i * 4]     = b0 * a00 + b1 * a10 + b2 * a20 + b3 * a30;
    out[outOffset + i * 4 + 1] = b0 * a01 + b1 * a11 + b2 * a21 + b3 * a31;
    out[outOffset + i * 4 + 2] = b0 * a02 + b1 * a12 + b2 * a22 + b3 * a32;
    out[outOffset + i * 4 + 3] = b0 * a03 + b1 * a13 + b2 * a23 + b3 * a33;
  }
}

/** out[outOffset..+16] = a * b. */
function multiplyMat4Direct(
  a: Float32Array, b: Float32Array,
  out: Float32Array, outOffset: number,
): void {
  const a00 = a[0],  a01 = a[1],  a02 = a[2],  a03 = a[3];
  const a10 = a[4],  a11 = a[5],  a12 = a[6],  a13 = a[7];
  const a20 = a[8],  a21 = a[9],  a22 = a[10], a23 = a[11];
  const a30 = a[12], a31 = a[13], a32 = a[14], a33 = a[15];
  for (let i = 0; i < 4; i++) {
    const b0 = b[i * 4], b1 = b[i * 4 + 1], b2 = b[i * 4 + 2], b3 = b[i * 4 + 3];
    out[outOffset + i * 4]     = b0 * a00 + b1 * a10 + b2 * a20 + b3 * a30;
    out[outOffset + i * 4 + 1] = b0 * a01 + b1 * a11 + b2 * a21 + b3 * a31;
    out[outOffset + i * 4 + 2] = b0 * a02 + b1 * a12 + b2 * a22 + b3 * a32;
    out[outOffset + i * 4 + 3] = b0 * a03 + b1 * a13 + b2 * a23 + b3 * a33;
  }
}
